import React, { useEffect, useState, useCallback } from 'react';
import api, { getImageUrl, getPlaceholder } from '../../api';
import { Loader, EmptyState, ConfirmDialog } from '../../components/Shared';
import { useToast } from '../../context/AppContext';

const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3MB per image

function ProjectForm({ project, services, onSave, onClose }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({
    title:             project?.title || '',
    description:       project?.description || '',
    short_description: project?.short_description || '',
    client_name:       project?.client_name || '',
    location:          project?.location || '',
    project_value:     project?.project_value || '',
    start_date:        project?.start_date?.split('T')[0] || '',
    end_date:          project?.end_date?.split('T')[0] || '',
    status:            project?.status || 'completed',
    is_featured:       project?.is_featured ?? 0,
    published:         project?.published ?? 1,
  });
  const [thumbnail, setThumbnail]           = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [gallery, setGallery]               = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [saving, setSaving]                 = useState(false);
  const [projectImages, setProjectImages]   = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [deletingImgId, setDeletingImgId]   = useState(null);

  // Load existing project data when editing
  useEffect(() => {
    if (project) {
      api.get(`/projects/${project.slug || project.id}`).then(r => {
        const p = r.data.data;
        setSelectedServices((p.services || []).map(s => s.id));
        setProjectImages(p.images || []);
      }).catch(() => {
        // Fallback: load images separately
        api.get(`/projects/${project.id}/images`).then(r => {
          setProjectImages(r.data.data || []);
        }).catch(() => {});
      });
    }
  }, [project]);

  // Build/revoke object URLs for newly selected gallery files so they can be previewed before upload
  useEffect(() => {
    const urls = gallery.map(f => URL.createObjectURL(f));
    setGalleryPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [gallery]);

  const toggleService = id => setSelectedServices(prev =>
    prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
  );

  const handleThumbnailChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      addToast('Image size must not exceed 3MB', 'error');
      e.target.value = '';
      return;
    }
    setThumbnail(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (project) {
        // Always use FormData for edit so we can include thumbnail in same request
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('services', JSON.stringify(selectedServices));
        if (thumbnail) fd.append('thumbnail', thumbnail);

        // Use POST with _method override for multipart PUT
        // (Apache/PHP doesn't read multipart body for PUT)
        // So we split: PUT JSON for text, then POST for files
        await api.put(`/projects/${project.id}`, {
          ...form,
          services: selectedServices,
        });

        // Upload new thumbnail separately if provided
        if (thumbnail) {
          const thumbFd = new FormData();
          thumbFd.append('thumbnail', thumbnail);
          await api.post(`/projects/${project.id}/thumbnail`, thumbFd);
        }

        // Upload new gallery images separately
        if (gallery.length > 0) {
          const galFd = new FormData();
          gallery.forEach(f => galFd.append('images[]', f));
          await api.post(`/projects/${project.id}/images`, galFd);
        }

        addToast('Project updated successfully!', 'success');
      } else {
        // Create: single multipart request
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('services', JSON.stringify(selectedServices));
        if (thumbnail) fd.append('thumbnail', thumbnail);
        gallery.forEach(f => fd.append('gallery[]', f));
        await api.post('/projects', fd);
        addToast('Project created successfully!', 'success');
      }
      onSave();
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to save project', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete a single gallery image — confirmed before calling
  const deleteImage = async (img) => {
    setDeletingImgId(img.id);
    try {
      await api.delete(`/projects/${project.id}/images/${img.id}`);
      setProjectImages(prev => prev.filter(i => i.id !== img.id));
      addToast('Image deleted', 'success');
    } catch (err) {
      addToast(err.response?.data?.message || 'Failed to delete image', 'error');
    } finally {
      setDeletingImgId(null);
    }
  };


  return (
    <div className="modal-overlay" onClick={onClose} style={{ padding: '12px' }}>
      <div className="modal-box" style={{ maxWidth: 860, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '16px 20px' }}>
          <h5 style={{ color: '#1A1A1A', margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(1rem, 4vw, 1.2rem)' }}>
            {project ? 'Edit Project' : 'New Project'}
          </h5>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8A8A8A', fontSize: 22, cursor: 'pointer' }}>
            <i className="bi bi-x" />
          </button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ padding: '16px 20px' }}>
            <div className="row g-3">

              {/* Basic Info */}
              <div className="col-12">
                <label className="form-label-styled">Project Title *</label>
                <input className="form-control-styled" required placeholder="e.g. Skyline Tower Complex"
                  value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label-styled">Full Description *</label>
                <textarea className="form-control-styled" rows={4} required placeholder="Detailed project description..."
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label-styled">Short Description <span style={{fontWeight:400,color:'#aaa'}}>(shown on cards)</span></label>
                <textarea className="form-control-styled" rows={2} placeholder="Brief one-liner for project cards..."
                  value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))} />
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label-styled">Client Name</label>
                <input className="form-control-styled" placeholder="Client or company name"
                  value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} />
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label-styled">Location</label>
                <input className="form-control-styled" placeholder="City, Country"
                  value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
              </div>
              <div className="col-12 col-sm-4">
                <label className="form-label-styled">Project Value (£)</label>
                <input type="number" className="form-control-styled" placeholder="0.00"
                  value={form.project_value} onChange={e => setForm(f => ({ ...f, project_value: e.target.value }))} />
              </div>
              <div className="col-6 col-sm-4">
                <label className="form-label-styled">Start Date</label>
                <input type="date" className="form-control-styled"
                  value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div className="col-6 col-sm-4">
                <label className="form-label-styled">End Date</label>
                <input type="date" className="form-control-styled"
                  value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
              <div className="col-6 col-sm-4">
                <label className="form-label-styled">Status</label>
                <select className="form-control-styled" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="completed">Completed</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </div>
              <div className="col-6 col-sm-4">
                <label className="form-label-styled">Featured</label>
                <select className="form-control-styled" value={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: parseInt(e.target.value) }))}>
                  <option value={0}>No</option>
                  <option value={1}>Yes</option>
                </select>
              </div>
              <div className="col-12 col-sm-4">
                <label className="form-label-styled">Visibility</label>
                <select className="form-control-styled" value={form.published} onChange={e => setForm(f => ({ ...f, published: parseInt(e.target.value) }))}>
                  <option value={1}>Published</option>
                  <option value={0}>Draft</option>
                </select>
              </div>

              {/* Services */}
              <div className="col-12">
                <label className="form-label-styled">Services Included</label>
                <select className="form-control-styled" value=""
                  onChange={e => { if (e.target.value) toggleService(parseInt(e.target.value)); }}>
                  <option value="">— Select a service to add —</option>
                  {services.filter(s => !selectedServices.includes(s.id)).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {selectedServices.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {selectedServices.map(id => {
                      const s = services.find(sv => sv.id === id);
                      if (!s) return null;
                      return (
                        <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 20, background: '#C9A84C', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                          <i className={`bi ${s.icon}`} />{s.name}
                          <button type="button" onClick={() => toggleService(id)}
                            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', fontSize: 14, lineHeight: 1 }}>
                            <i className="bi bi-x" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cover Thumbnail */}
              <div className="col-12 col-sm-6">
                <label className="form-label-styled">
                  Cover / Thumbnail Image
                  {project && <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400, marginLeft: 6 }}>Upload new to replace</span>}
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {(thumbnailPreview || project?.thumbnail) && (
                    <img
                      src={thumbnailPreview || getImageUrl('projects', project.thumbnail)}
                      alt="thumbnail"
                      style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '2px solid #DEE2E6', flexShrink: 0 }}
                      onError={e => e.target.src = getPlaceholder(80, 60)}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <input type="file" className="form-control-styled" accept="image/*"
                      style={{ padding: '7px 10px' }} onChange={handleThumbnailChange} />
                    {thumbnailPreview && (
                      <div style={{ fontSize: 11, color: '#27AE60', marginTop: 4 }}>
                        <i className="bi bi-check-circle me-1" />New thumbnail selected
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Gallery Upload */}
              <div className="col-12 col-sm-6">
                <label className="form-label-styled">Add Gallery Images <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>(multiple)</span></label>
                <input type="file" className="form-control-styled" accept="image/*" multiple
                  style={{ padding: '7px 10px' }}
                  onChange={e => {
                    const files = [...e.target.files]; // capture before clearing the input
                    const valid = files.filter(f => f.size <= MAX_IMAGE_SIZE);
                    const oversizedCount = files.length - valid.length;
                    if (oversizedCount > 0) {
                      addToast(`${oversizedCount} image(s) exceed 3MB and were not added`, 'error');
                    }
                    if (valid.length > 0) setGallery(prev => [...prev, ...valid]);
                    e.target.value = ''; // allow re-selecting the same file later
                  }} />
                {gallery.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: '#C9A84C', marginTop: 4, marginBottom: 8 }}>
                      <i className="bi bi-images me-1" />{gallery.length} file(s) ready to upload
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {gallery.map((file, i) => (
                        <div key={i} style={{ position: 'relative', width: 80, height: 64, borderRadius: 6, overflow: 'hidden', border: '2px solid #C9A84C', flexShrink: 0 }}>
                          <img
                            src={galleryPreviews[i]}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button type="button" title="Remove"
                            onClick={() => setGallery(prev => prev.filter((_, idx) => idx !== i))}
                            style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(192,57,43,0.9)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff' }}>
                            <i className="bi bi-x" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Existing Gallery Images */}
              {projectImages.length > 0 && (
                <div className="col-12">
                  <label className="form-label-styled">
                    Current Gallery
                    <span style={{ fontWeight: 400, color: '#aaa', fontSize: 11, marginLeft: 6 }}>
                      ({projectImages.length} image{projectImages.length !== 1 ? 's' : ''}) — click × to remove
                    </span>
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {projectImages.map(img => {
                      const isThumb = project?.thumbnail === img.filename;
                      return (
                        <div key={img.id} style={{ position: 'relative', width: 80, height: 64, borderRadius: 6, overflow: 'hidden', border: isThumb ? '2px solid #C9A84C' : '2px solid #DEE2E6', flexShrink: 0 }}>
                          <img
                            src={getImageUrl('projects', img.filename)}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={e => e.target.src = getPlaceholder(80, 64)}
                          />
                          <button type="button" title="Delete image"
                            onClick={() => deleteImage(img)}
                            disabled={deletingImgId === img.id}
                            style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(192,57,43,0.9)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff' }}>
                            {deletingImgId === img.id
                              ? <div style={{ width: 10, height: 10, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                              : <i className="bi bi-x" />}
                          </button>
                          {isThumb && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(201,168,76,0.9)', fontSize: 9, fontWeight: 700, color: '#fff', textAlign: 'center', padding: '2px 0', letterSpacing: 1 }}>
                              COVER
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
          <div className="modal-footer" style={{ padding: '14px 20px' }}>
            <button type="button" onClick={onClose} className="btn-outline-secondary-styled">Cancel</button>
            <button type="submit" className="btn-primary-styled" disabled={saving}>
              {saving
                ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block', marginRight: 8 }} />Saving...</>
                : <><i className="bi bi-check2 me-1" />{project ? 'Update Project' : 'Create Project'}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProjectsPage() {
  const [projects, setProjects]         = useState([]);
  const [services, setServices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [search, setSearch]             = useState('');
  const [editProject, setEditProject]   = useState(null);
  const [showForm, setShowForm]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { addToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/projects?page=${page}&limit=10&admin=1`)
      .then(r => {
        const d = r.data.data;
        setProjects(d.projects || []);
        setTotalPages(d.total_pages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/services').then(r => setServices(r.data.data || [])).catch(() => {}); }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/projects/${confirmDelete.id}`);
      addToast('Project deleted', 'success');
      setConfirmDelete(null);
      load();
    } catch {
      addToast('Failed to delete project', 'error');
    }
  };

  const filtered = projects.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', color: '#1A1A1A', marginBottom: 4 }}>Projects</h2>
          <p style={{ color: '#6C757D', margin: 0, fontSize: '0.875rem' }}>Manage your construction portfolio</p>
        </div>
        <button onClick={() => { setEditProject(null); setShowForm(true); }} className="btn-primary-styled">
          <i className="bi bi-plus-lg me-1" /> New Project
        </button>
      </div>

      {/* Search bar */}
      <div className="admin-card mb-3" style={{ padding: '14px 20px' }}>
        <div style={{ position: 'relative', maxWidth: 380 }}>
          <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: 14 }} />
          <input className="form-control-styled" placeholder="Search projects..." style={{ paddingLeft: 36 }}
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? <div style={{ padding: 32 }}><Loader /></div> : filtered.length === 0 ? (
        <EmptyState icon="bi-building" title="No projects yet" subtitle="Create your first project!" />
      ) : (
        <div className="row g-3">
          {filtered.map(p => (
            <div key={p.id} className="col-12 col-md-6 col-xl-4">
              <div className="admin-card p-0 h-100" style={{ position: 'relative', overflow: 'hidden' }}>
                {/* Action buttons */}
                <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, zIndex: 2 }}>
                  <button onClick={() => { setEditProject(p); setShowForm(true); }}
                    style={{ background: 'rgba(235,244,253,0.92)', border: '1px solid #BDE0F8', color: '#2980B9', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                    <i className="bi bi-pencil" />
                  </button>
                  <button onClick={() => setConfirmDelete(p)}
                    style={{ background: 'rgba(253,236,234,0.92)', border: '1px solid #F5C6CB', color: '#C0392B', padding: '5px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                    <i className="bi bi-trash" />
                  </button>
                </div>

                {/* Thumbnail */}
                <div style={{ width: '100%', height: 150, background: '#F0F0F0', overflow: 'hidden' }}>
                  <img
                    src={p.thumbnail ? getImageUrl('projects', p.thumbnail) : getPlaceholder(400, 150)}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => e.target.src = getPlaceholder(400, 150)}
                  />
                </div>

                {/* Content */}
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ fontWeight: 700, color: '#1A1A1A', fontSize: '0.9rem', marginBottom: 3, paddingRight: 60 }}>{p.title}</div>
                  {p.client_name && <div style={{ color: '#6C757D', fontSize: '0.78rem', marginBottom: 10 }}>{p.client_name}</div>}

                  {/* Meta row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    {p.location && (
                      <span style={{ color: '#6C757D', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="bi bi-geo-alt" style={{ fontSize: 11 }} />{p.location}
                      </span>
                    )}
                    <span style={{ color: '#6C757D', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="bi bi-eye" style={{ fontSize: 11 }} />{p.views || 0}
                    </span>
                  </div>

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span className={
                      p.status === 'completed' ? 'badge-success-light' :
                      p.status === 'ongoing'   ? 'badge-warning-light' : 'badge-secondary-light'
                    }>{p.status}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: p.published ? '#27AE60' : '#DC3545' }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.published ? '#27AE60' : '#DC3545', display: 'inline-block' }} />
                      {p.published ? 'Live' : 'Draft'}
                    </span>
                    {p.is_featured === 1 && <span className="badge-gold-light"><i className="bi bi-star-fill me-1" />Featured</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination pagination-light">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p - 1)}><i className="bi bi-chevron-left" /></button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p + 1)}><i className="bi bi-chevron-right" /></button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {showForm && (
        <ProjectForm
          project={editProject}
          services={services}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); load(); }}
        />
      )}
      {confirmDelete && (
        <ConfirmDialog
          message={`Delete "${confirmDelete.title}"? All associated images will also be removed permanently.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
